import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { CONTACT } from "../../data";
import { useLang } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  company: "",
  name: "",
  email: "",
  phone: "",
  trainees: "",
  courses: [],
  language: "English",
  referral_source: "",
  referral_name: "",
  location: "",
  street: "",
  city: "",
  state: "",
  zip_code: "",
  preferred_date: "",
  message: "",
};

const REFERRAL = [
  { key: "social", en: "Social Media", es: "Redes Sociales" },
  { key: "flyer", en: "Flyer / Location", es: "Volante / Ubicación" },
  { key: "word", en: "Word of Mouth", es: "De Boca en Boca" },
  { key: "person", en: "Referred by a Person", es: "Me Refirió una Persona" },
  { key: "google", en: "Google Search", es: "Búsqueda en Google" },
  { key: "other", en: "Other", es: "Otro" },
];

export const QuoteForm = () => {
  const { t, lang } = useLang();
  const q = t.quote;
  const [form, setForm] = useState(EMPTY);
  const [refKey, setRefKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCourse = (c) =>
    setForm((f) => ({
      ...f,
      courses: f.courses.includes(c) ? f.courses.filter((x) => x !== c) : [...f.courses, c],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error(q.toastMissing);
      return;
    }
    if (!refKey) {
      toast.error(q.toastReferral);
      return;
    }
    if (refKey === "person" && !form.referral_name.trim()) {
      toast.error(q.toastReferralName);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      if (typeof window !== "undefined") {
        const payload = { courses: form.courses, language: form.language, trainees: form.trainees };
        if (window.dataLayer) window.dataLayer.push({ event: "quote_submit", ...payload });
        if (typeof window.gtag === "function") window.gtag("event", "quote_submit", payload);
      }
      setDone(true);
      toast.success(q.toastOk);
      setForm(EMPTY);
      setRefKey("");
    } catch (err) {
      toast.error(`${q.toastErr} ${CONTACT.phone}`);
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full bg-[#060c1f] border border-white/10 focus:border-amber-400 rounded-lg px-4 py-3 text-white placeholder:text-slate-600 outline-none transition-colors text-sm";

  return (
    <section id="quote" data-testid="quote-section" className="relative py-24 sm:py-32 bg-[#060c1f]">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-4">
            <SectionLabel num="[ 08 ]">{q.label}</SectionLabel>
            <h2 className="font-head font-extrabold uppercase tracking-tight text-white text-4xl sm:text-5xl leading-[0.9]">
              {q.title1}
              <span className="block text-gold-grad">{q.title2}</span>
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed">{q.intro}</p>
            <div className="mt-8 space-y-3">
              <a href={`tel:${CONTACT.phoneRaw}`} className="block font-head font-bold text-2xl text-white hover:text-amber-400 transition-colors" data-testid="quote-phone-link">
                {CONTACT.phone}
              </a>
              <a href={`mailto:${CONTACT.email}`} className="block text-sm text-slate-400 hover:text-amber-400 transition-colors" data-testid="quote-email-link">
                {CONTACT.email}
              </a>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-8" delay={0.1}>
            <div className="rounded-2xl card-hairline p-6 sm:p-9">
              {done ? (
                <div className="py-14 text-center" data-testid="quote-success">
                  <CheckCircle2 size={56} className="mx-auto text-emerald-400" />
                  <h3 className="mt-5 font-head font-bold uppercase tracking-wide text-2xl text-white">{q.successTitle}</h3>
                  <p className="mt-2 text-slate-400 max-w-sm mx-auto">
                    {q.successBody} {CONTACT.phone}.
                  </p>
                  <button onClick={() => setDone(false)} className="mt-6 text-sm font-head font-bold uppercase tracking-wide text-amber-400" data-testid="quote-reset-btn">
                    {q.another}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="quote-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label={q.name}>
                      <input className={input} data-testid="quote-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Operator" />
                    </Field>
                    <Field label={q.company}>
                      <input className={input} data-testid="quote-company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Acme Logistics" />
                    </Field>
                    <Field label={q.email}>
                      <input type="email" className={input} data-testid="quote-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" />
                    </Field>
                    <Field label={q.phone}>
                      <input className={input} data-testid="quote-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(209) 000-0000" />
                    </Field>
                    <Field label={q.trainees}>
                      <input className={input} data-testid="quote-trainees" value={form.trainees} onChange={(e) => set("trainees", e.target.value)} placeholder="8" />
                    </Field>
                    <Field label={q.language}>
                      <select className={input} data-testid="quote-language" value={form.language} onChange={(e) => set("language", e.target.value)}>
                        <option value="English">{q.langOptions[0]}</option>
                        <option value="Spanish">{q.langOptions[1]}</option>
                        <option value="Bilingual">{q.langOptions[2]}</option>
                      </select>
                    </Field>
                  </div>

                  <Field label={q.courses}>
                    <div className="flex flex-wrap gap-2">
                      {q.courseOptions.map((c) => {
                        const on = form.courses.includes(c);
                        return (
                          <button
                            type="button"
                            key={c}
                            data-testid={`quote-course-${c}`}
                            onClick={() => toggleCourse(c)}
                            className={`text-sm px-4 py-2 rounded-full border transition-colors ${
                              on ? "bg-amber-400 border-amber-400 text-[#060c1f] font-semibold" : "border-white/15 text-slate-300 hover:border-white/40"
                            }`}
                          >
                            {c}
                          </button>
                        );
                      })}
                    </div>
                  </Field>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label={q.referralLabel}>
                      <select
                        className={input}
                        data-testid="quote-referral"
                        value={refKey}
                        onChange={(e) => {
                          const k = e.target.value;
                          setRefKey(k);
                          const opt = REFERRAL.find((r) => r.key === k);
                          set("referral_source", opt ? opt.en : "");
                          if (k !== "person") set("referral_name", "");
                        }}
                      >
                        <option value="">{q.referralPlaceholder}</option>
                        {REFERRAL.map((r) => (
                          <option key={r.key} value={r.key}>{lang === "es" ? r.es : r.en}</option>
                        ))}
                      </select>
                    </Field>
                    {refKey === "person" && (
                      <Field label={q.referralPersonLabel}>
                        <input
                          className={input}
                          data-testid="quote-referral-name"
                          value={form.referral_name}
                          onChange={(e) => set("referral_name", e.target.value)}
                          placeholder="Jane Salesperson"
                        />
                      </Field>
                    )}
                  </div>

                  <Field label={q.street}>
                    <input className={input} data-testid="quote-street" value={form.street} onChange={(e) => set("street", e.target.value)} placeholder="123 Warehouse Way" />
                  </Field>

                  <div className="grid sm:grid-cols-3 gap-4">
                    <Field label={q.city}>
                      <input className={input} data-testid="quote-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Modesto" />
                    </Field>
                    <Field label={q.state}>
                      <input className={input} data-testid="quote-state" value={form.state} onChange={(e) => set("state", e.target.value)} placeholder="CA" />
                    </Field>
                    <Field label={q.zip}>
                      <input className={input} data-testid="quote-zip" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} placeholder="95350" />
                    </Field>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label={q.date}>
                      <input type="date" className={`${input} [color-scheme:dark]`} data-testid="quote-date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} />
                    </Field>
                  </div>

                  <Field label={q.more}>
                    <textarea rows={3} className={`${input} resize-none`} data-testid="quote-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder={q.morePh} />
                  </Field>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="quote-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-[#060c1f] font-head font-bold uppercase tracking-wide py-4 rounded-md transition-colors"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> {q.sending}
                      </>
                    ) : (
                      <>
                        {q.submit} <Send size={17} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block font-mono-x text-[11px] uppercase tracking-widest text-slate-400 mb-2">{label}</span>
    {children}
  </label>
);
